import { IconPlus, IconTrash } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { CreateDashboardModal } from '../components/CreateDashboardModal';
import { DashboardRenameModal } from '../components/DashboardRenameModal';
import { DashboardRow } from '../components/DashboardRow';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { Panel } from '../components/Panel';
import { QueryBoundary } from '../components/QueryBoundary';
import { Toast } from '../components/Toast';
import { Button } from '../components/ui/Button';
import { t } from '../i18n';
import { UserFacingError } from '../lib/api';
import { createDashboardActions } from '../lib/dashboard-actions';
import {
  boardNameTaken,
  createDashboard,
  type NewBoardForm,
  noteBoardBody,
} from '../lib/dashboard-writes';
import { groupDashboardsByScope, useDashboards } from '../lib/dashboards';
import { createMutation } from '../lib/mutation';
import { SESSION_TOKEN } from '../lib/session';

// Dashboards index — the SCOPE ROUTER (arc 101 slice 09, task #81) and, since
// #1033, the board AUTHORING surface: create / rename / delete over the write
// half of `/v1/dashboards`, which until now had no caller outside MCP.
//
// Every write is followed by a REFETCH, never a patch from the response echo:
// the list is a shared-∪-own projection the server computes, so what an author
// sees after a create is a read, not an assumption. `token` is injectable for tests.
export function Dashboards(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token ?? SESSION_TOKEN;
  const list = useDashboards(token);
  // `reload()`: the index is walked whole, and a plain refetch would re-read only
  // the last page the walk keyed to (`lib/paged.ts`).
  const reread = (): void => list.reload();

  const [createOpen, setCreateOpen] = createSignal(false);
  const actions = createDashboardActions({ token, onRenamed: reread, onDeleted: reread });

  const onCreate = createMutation({
    call: async (form: NewBoardForm) => {
      // A taken name 409s server-side (#1099) and the route already holds the rows
      // this caller can see — so answer the collision from the list (`boardNameTaken`)
      // instead of bouncing them off that error. A mirror, not enforcement: what
      // this list can't see stays the server's call. It REJECTS rather than
      // returning early so the notice rides the one error channel this form has;
      // `errorReason` renders a `UserFacingError`'s own message (and only that
      // kind of non-API error's), so the sentence is the same one it always was,
      // and no request leaves the browser.
      if (boardNameTaken(list.rows(), form)) {
        throw new UserFacingError(t('dashboards.create_name_taken', { name: form.name.trim() }));
      }
      return createDashboard(noteBoardBody(form), token);
    },
    fallback: t('dashboards.create_failed'),
    onSuccess: () => {
      setCreateOpen(false);
      reread();
    },
  });

  const openCreate = (): void => {
    onCreate.clearError();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title={t('dashboards.title')}
        desc={t('dashboards.subtitle')}
        actions={
          <Show when={token !== null}>
            <Button onClick={openCreate}>
              <IconPlus />
              {t('dashboards.create')}
            </Button>
          </Show>
        }
      />
      <QueryBoundary
        query={list.view}
        isEmpty={(rows) => rows.length === 0}
        loading={<CardGridSkeleton listClass="dashboards__list" />}
        empty={
          <EmptyState
            message={t('dashboards.empty')}
            {...(token !== null ? { ctaLabel: t('dashboards.create'), onCta: openCreate } : {})}
          />
        }
      >
        {(rows) => (
          <For each={groupDashboardsByScope(rows)}>
            {(group) => (
              <Panel title={t(`dashboards.scope_${group.scope}`)}>
                <ul class="row-list">
                  <For each={group.dashboards}>
                    {(d) => (
                      <DashboardRow
                        board={d}
                        onRename={actions.askRename}
                        onDelete={actions.askDelete}
                      />
                    )}
                  </For>
                </ul>
              </Panel>
            )}
          </For>
        )}
      </QueryBoundary>

      <CreateDashboardModal
        open={createOpen()}
        pending={onCreate.pending()}
        error={onCreate.error()}
        onCreate={(form) => void onCreate.run(form)}
        onClose={() => setCreateOpen(false)}
      />
      <DashboardRenameModal
        open={actions.renameTarget() !== null}
        name={actions.renameTarget()?.name ?? ''}
        pending={actions.pending()}
        error={actions.renameError()}
        onRename={actions.submitRename}
        onClose={actions.close}
      />
      <ConfirmDialog
        open={actions.deleteTarget() !== null}
        title={t('dashboards.delete_title')}
        desc={t('dashboards.delete_body', { name: actions.deleteTarget()?.name ?? '' })}
        media={<IconTrash />}
        cancelLabel={t('dashboards.cancel')}
        actionLabel={t('dashboards.delete_confirm')}
        destructive
        onCancel={actions.close}
        onConfirm={actions.confirmDelete}
      />
      <Toast message={actions.deleteError()} tone="error" onDismiss={actions.clearError} />
    </>
  );
}
